/**
 * The brand mark, shown in the header, footer and mobile menu.
 *
 * The logo is whatever an administrator uploads under Settings, and the
 * uploaded artwork already contains the business name — so no wordmark is
 * rendered beside it. Repeating the name in markup next to a logo that also
 * says it read as a duplicate.
 *
 * Two fallbacks, because a header with nothing in it is the worst outcome:
 *   - no logo configured  -> the typeset wordmark
 *   - logo fails to load  -> same, via onError
 *
 * The second matters more than it looks. The original artwork was hosted on
 * the platform this site was prototyped on, and any externally hosted logo can
 * disappear without warning.
 */
import { useState } from "react";
import { useBrand } from "../../context/SiteSettingsContext";

/** Typeset fallback, matching the original lockup. */
function Wordmark({ size }) {
  const scale =
    size === "lg"
      ? { primary: "text-[18px]", secondary: "text-[11px] tracking-[0.32em]" }
      : { primary: "text-[13px] md:text-[15px]", secondary: "text-[10px] md:text-[11px] tracking-[0.28em]" };

  return (
    <span className="font-display leading-none" data-testid="brand-wordmark">
      <span className={`block ${scale.primary} font-extrabold tracking-[0.02em] text-white`}>
        EMILIO&rsquo;S
      </span>
      <span className={`block ${scale.secondary} font-semibold text-emerald`}>
        SOLUTIONS
      </span>
    </span>
  );
}

export default function BrandLogo({ className = "h-10", size = "md" }) {
  const brand = useBrand();
  const [failed, setFailed] = useState(false);

  const logo = brand?.logo;

  if (!logo || failed) return <Wordmark size={size} />;

  return (
    <img
      src={logo}
      // Carries the business name so a failed load still says who this is,
      // and screen readers get the brand rather than nothing.
      alt={brand?.name || "Emilio's Solutions"}
      onError={() => setFailed(true)}
      // Width is auto: the uploaded logo is a landscape lockup, and forcing it
      // into a square box would render it a fraction of its intended size.
      className={`${className} w-auto max-w-[220px] object-contain object-left`}
      data-testid="brand-logo"
    />
  );
}
